import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Box, Paper, Chip, CircularProgress, FormControlLabel, Checkbox, Grid } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { getData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';
import { getStatusColor } from '../../../../utils/statusColor';
import EmployeeExitDetails from './employeeExitDetails';
import EmployeeRehire from './employeeRehire';

const ExitRehire = ({ navigationContext, rehireChecklist, onUpdateRehireChecklist, onStartRehireVerification, onRehireCompleted }) => {
    const [searchParams] = useSearchParams();
    const empIdFromUrl = searchParams.get("employeeId");

    const [userData, setUserData] = useState(null);
    const [exitAndRehireData, setExitAndRehireData] = useState(null); // Store API response
    const [isFetching, setIsFetching] = useState(true);

    const [isTerminationMode, setIsTerminationMode] = useState(false);
    const [hasActiveExit, setHasActiveExit] = useState(false);
    const [isRehireMode, setIsRehireMode] = useState(false);

    useEffect(() => {
        if (navigationContext?.returnToRehire) {
            setIsRehireMode(true);
        }
    }, [navigationContext]);

    useEffect(() => {
        if (rehireChecklist && Object.values(rehireChecklist).some(Boolean)) {
            setIsRehireMode(true);
        }
    }, [rehireChecklist]);

    const fetchUserDetails = useCallback(async () => {
        if (!empIdFromUrl) return;
        try {
            const response = await getData(`${API_ENDPOINTS.fetchUserDetails}?userId=${empIdFromUrl}`);
            if (response?.status === "SUCCESS") {
                setUserData(response.message.user);
            }
        } catch (error) {
            console.error("Error fetching user:", error);
        }
    }, [empIdFromUrl]);

    const fetchExitRehireDetails = useCallback(async () => {
        if (!empIdFromUrl) return;
        try {
            const response = await getData(API_ENDPOINTS.getEmployeeExitAndRehireDetails(empIdFromUrl));
            if (response?.status === "SUCCESS") {
                const result = response.result;
                const latestExit = result?.exitDetails?.[0] ?? null;
                const rehireCompleted = Boolean(
                    result?.personalDetails?.rehire_date ||
                    result?.personalDetails?.rehire_reason ||
                    latestExit?.rehire_date ||
                    latestExit?.rehire_reason
                );

                setExitAndRehireData(result);
                setHasActiveExit(Boolean(latestExit && !rehireCompleted));

                // Reset UI state after loading data
                setIsTerminationMode(false);
                if (!rehireCompleted) {
                    setIsRehireMode(false);
                }
            }
        } catch (error) {
            console.error("Error fetching exit/rehire details:", error);
        }
    }, [empIdFromUrl]);

    useEffect(() => {
        const loadData = async () => {
            setIsFetching(true);
            await Promise.all([fetchUserDetails(), fetchExitRehireDetails()]);
            setIsFetching(false);
        };
        loadData();
    }, [fetchUserDetails, fetchExitRehireDetails]);

    // Format Status for UI
    const rawStatus = userData?.status || 'unknown';
    const formattedStatus = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();
    const statusStyles = getStatusColor(formattedStatus);

    // Derived Logic for section visibility
    const hasExitRecord = Boolean(exitAndRehireData?.exitDetails?.length > 0);
    const hasRehireDetails = Boolean(
        exitAndRehireData?.personalDetails?.rehire_date ||
        exitAndRehireData?.personalDetails?.rehire_reason
    );
    const showRehireSection = hasActiveExit;
    const showTerminationSection = true;

    const handleExitDataLoaded = (hasActive) => {
        setHasActiveExit(hasActive);
    };

    const handleRehireSuccess = useCallback(async () => {
        await Promise.all([fetchUserDetails(), fetchExitRehireDetails()]);
        setIsRehireMode(false);
        setIsTerminationMode(false);
        // Notify parent to refresh user details and update status chip
        if (onRehireCompleted) {
            onRehireCompleted();
        }
    }, [fetchUserDetails, fetchExitRehireDetails, onRehireCompleted]);

    if (isFetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ my: 2 }}>
                <Typography variant="h6" fontWeight={600} component="h2" gutterBottom>
                    Employee Status Management
                </Typography>

                <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Current Employee Status:
                        </Typography>

                        <Chip
                            label={formattedStatus.toUpperCase()}
                            sx={{
                                fontWeight: 600,
                                backgroundColor: statusStyles.bg,
                                color: statusStyles.color,
                                border: `1px solid ${statusStyles.border}`,
                                '& .MuiChip-label': { px: 2 }
                            }}
                            variant="outlined"
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                        {/* 1. TERMINATION SECTION */}
                        {showTerminationSection && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isTerminationMode}
                                        onChange={(e) => setIsTerminationMode(e.target.checked)}
                                        color="error"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body1" fontWeight={600}>
                                            Start Termination or Resignation Process
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Check this to initiate termination/resignation flow
                                        </Typography>
                                    </Box>
                                }
                            />
                        )}

                        {/* 2. REHIRE SECTION */}
                        {showRehireSection && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isRehireMode}
                                        onChange={(e) => setIsRehireMode(e.target.checked)}
                                        color="success"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body1" fontWeight={600} color="success.main">
                                            Start Rehire Process
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            This employee has an existing exit record. Check this to begin rehiring.
                                        </Typography>
                                    </Box>
                                }
                            />
                        )}
                    </Box>
                </Paper>

                {/* Previous data summaries */}
                {(hasExitRecord || hasRehireDetails) && (
                    <Box sx={{ mt: 3, display: 'grid', gap: 3 }}>
                        {hasExitRecord && (
                            <Paper elevation={1} sx={{ p: 3, border: '1px solid', borderColor: 'primary.light' }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    Previous Termination or Resignation Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                            Type
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                                            {exitAndRehireData?.exitDetails?.[0]?.type || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                            Final Exit Date
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                                            {exitAndRehireData?.exitDetails?.[0]?.final_date_of_exit || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                            Reason
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            {exitAndRehireData?.exitDetails?.[0]?.reason_for_exit || 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
                        {hasRehireDetails && (
                            <Paper elevation={1} sx={{ p: 3, border: '1px solid', borderColor: 'success.light' }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: 'success.main' }}>
                                    Previous Rehire Details
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                            Rehire Date
                                        </Typography>
                                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                                            {exitAndRehireData?.personalDetails?.rehire_date || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                                            Reason for Rehire
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            {exitAndRehireData?.personalDetails?.rehire_reason || 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        )}
                    </Box>
                )}

                {/* 3. CONDITIONAL CONTENT RENDERING */}

                {isTerminationMode && (
                    <Box sx={{ mt: 3 }}>
                        <EmployeeExitDetails
                            userData={userData}
                            initialData={exitAndRehireData?.exitDetails?.[0]}
                            onDataLoaded={handleExitDataLoaded}
                            onExitSaved={fetchExitRehireDetails}
                            showEntryFields={true}
                        />
                    </Box>
                )}

                {isRehireMode && (
                    <Box sx={{ mt: 3 }}>
                        <EmployeeRehire
                            initialData={exitAndRehireData?.exitDetails?.[0]}
                            navigationContext={navigationContext}
                            rehireChecklist={rehireChecklist}
                            onUpdateRehireChecklist={onUpdateRehireChecklist}
                            onStartRehireVerification={onStartRehireVerification}
                            onRehireSuccess={handleRehireSuccess}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ExitRehire;